import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  ProductCatalogIcon,
  CheckCircleIcon,
  ClockIcon,
  CautionIcon,
  CancelIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import Pagination from '../../../component/shared/Pagination';
import OrderDetailsModal from '../../../component/admin/orders/OrderDetailsModal';
import AdminLayout from '../layout';
import adminOrderService from '../../../services/admin/admin-orderService';

const OrdersTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const [stats, setStats] = useState({
    totalOrders: 0,
    inProductionOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 1,
    totalItems: 0
  });

  const [selectedOrder, setSelectedOrder] = useState(null);

  /* ───────── Stats cards ───────── */
  const statsCards = [
    {
      label: 'Total Orders',
      value: stats.totalOrders,
      icon: <ProductCatalogIcon />,
      color: 'bg-[#F4E7F1] text-[#8F2F84]'
    },
    {
      label: 'In production',
      value: stats.inProductionOrders,
      icon: <ClockIcon />,
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Delivered',
      value: stats.deliveredOrders,
      icon: <CheckCircleIcon />,
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    },
    {
      label: 'Cancelled',
      value: stats.cancelledOrders,
      icon: <CancelIcon />,
      color: 'bg-[#F4E7E7] text-[#A82121]'
    }
  ];

  /* ───────── Status filter options ───────── */
  const statusFilters = [
    { id: 'all', label: 'All Status' },
    { id: 'pending', label: 'Pending' },
    { id: 'processing', label: 'Processing' },
    { id: 'content_review', label: 'Content Review' },
    { id: 'in_production', label: 'In Production' },
    { id: 'shipped', label: 'Shipped' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  /* ───────── Fetch orders ───────── */
  const fetchOrders = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const res = await adminOrderService.getOrders({
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        });

        if (res.success) {
          setOrders(res.data.orders || []);
          setPagination((p) => ({
            ...p,
            currentPage: res.data.pagination.page,
            totalPages: res.data.pagination.pages,
            totalItems: res.data.pagination.total
          }));
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error('Fetch orders error:', err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusFilter, pagination.itemsPerPage]
  );

  /* ───────── Fetch stats ───────── */
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await adminOrderService.getStats();
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(pagination.currentPage);
  }, [fetchOrders, pagination.currentPage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  /* ───────── Debounce search/filter changes ───────── */
  useEffect(() => {
    const t = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchOrders(1);
      } else {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery, statusFilter, fetchOrders]);

  /* ───────── Helpers ───────── */
  const getStatusBadgeConfig = (status) => {
    const map = {
      pending: {
        bgColor: 'bg-[#EBEFFF]',
        textColor: 'text-[#194F9B]',
        dotColor: 'bg-[#194F9B]'
      },
      processing: {
        bgColor: 'bg-[#FFF4D6]',
        textColor: 'text-[#D8A100]',
        dotColor: 'bg-[#D8A100]'
      },
      content_review: {
        bgColor: 'bg-[#EBEFFF]',
        textColor: 'text-[#194F9B]',
        dotColor: 'bg-[#194F9B]'
      },
      in_production: {
        bgColor: 'bg-[#FFF4D6]',
        textColor: 'text-[#D8A100]',
        dotColor: 'bg-[#D8A100]'
      },
      shipped: {
        bgColor: 'bg-[#F2E7F4]',
        textColor: 'text-[#6F1197]',
        dotColor: 'bg-[#6F1197]'
      },
      delivered: {
        bgColor: 'bg-[#E7F4ED]',
        textColor: 'text-[#2F8F5B]',
        dotColor: 'bg-[#2F8F5B]'
      },
      cancelled: {
        bgColor: 'bg-[#F4E7E7]',
        textColor: 'text-[#A82121]',
        dotColor: 'bg-[#A82121]'
      }
    };
    return (
      map[status] || {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        dotColor: 'bg-gray-500'
      }
    );
  };

  const humanStatus = (status) =>
    (status || '')
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');

  const formatCurrency = (n) => `₦${Number(n || 0).toLocaleString()}`;

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : '';

  const getCustomerName = (order) =>
    `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim();

  const getProductLabel = (order) => {
    if (!order.items?.length) return '—';
    if (order.items.length === 1) return order.items[0].title;
    return `${order.items[0].title} +${order.items.length - 1} more`;
  };

  /* ───────── Handlers ───────── */
  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  const handleViewOrder = async (id) => {
    try {
      const res = await adminOrderService.getOrderById(id);
      if (res.success) {
        setSelectedOrder(res.data.order);
      }
    } catch (err) {
      console.error('Fetch order detail error:', err);
    }
  };

  /* ───────── Status badge ───────── */
  const getStatusBadge = (status, statusConfig) => {
    return (
      <span
        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs leading-5 font-semibold rounded-full ${statusConfig.bgColor} w-fit`}
      >
        <span
          className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${statusConfig.dotColor}`}
        />
        <span className={statusConfig.textColor}>{status}</span>
        <ArrowDown size={10} className={statusConfig.textColor} />
      </span>
    );
  };

  /* ───────── Mobile Filters ───────── */
  const MobileFiltersModal = () => (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setMobileFiltersOpen(false)}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={() => setMobileFiltersOpen(false)} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1C2A46] mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by order ID, customer, product..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1C2A46] mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setStatusFilter(filter.id);
                    setMobileFiltersOpen(false);
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={() => setMobileFiltersOpen(false)}
          className="w-full mt-6 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
        >
          Apply Filters
        </button>
      </div>
    </>
  );

  /* ───────── Render ───────── */
  return (
    <AdminLayout>
      <div className="px-2 sm:px-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm"
            >
              <div className="text-left">
                <div
                  className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
                    {statsLoading ? (
                      <span className="inline-block w-6 h-6 bg-gray-200 rounded animate-pulse" />
                    ) : (
                      stat.value
                    )}
                  </p>
                  <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-sm shadow-sm">
          {/* Header */}
          <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB]">
            {/* Desktop */}
            <div className="hidden md:flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                All Orders
              </h2>

              <div className="flex gap-3 lg:gap-4 items-center">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={18} className="text-[#6B635D]" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 pl-9 pr-3 border w-56 lg:w-64 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 pl-3 pr-7 border w-44 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  >
                    {statusFilters.map((filter) => (
                      <option key={filter.id} value={filter.id}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ArrowDown size={14} className="text-[#6B635D]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-medium text-[#0E1628]">
                  All Orders
                </h2>
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                >
                  <FilterIcon size={14} />
                  Filters
                </button>
              </div>

              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={16} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search by order ID, customer, product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-[700px] md:min-w-full divide-y divide-[#DBDBDB]">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider w-fit">
                    Status
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider w-20">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#DBDBDB]">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146] mx-auto" />
                      <p className="mt-3 text-sm text-gray-600">
                        Loading orders…
                      </p>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-12 text-sm text-gray-500"
                    >
                      No orders found
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr
                      key={order._id}
                      className="hover:bg-gray-50 transition-colors duration-150 text-left"
                    >
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {order.orderNumber}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {getCustomerName(order)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {getProductLabel(order)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#8A9099]">
                          {formatDate(order.createdAt)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {formatCurrency(order.total)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap w-fit">
                        {getStatusBadge(
                          humanStatus(order.orderStatus),
                          getStatusBadgeConfig(order.orderStatus)
                        )}
                      </td>
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap w-20">
                        <button
                          onClick={() => handleViewOrder(order._id)}
                          className="text-xs sm:text-sm font-normal text-secondary hover:text-[#C49660] transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Empty state (only when filters yield 0 on an empty list) */}
          {!loading && orders.length === 0 && (
            <div className="text-center py-8 sm:py-12">
              <p className="text-[#6B635D] text-sm">
                No orders found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
                className="mt-3 text-[#D2A146] text-sm hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && !loading && (
            <div className="px-4 sm:px-6 py-4 border-t border-[#DBDBDB]">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-[#6B635D] order-2 sm:order-1">
                  Showing {orders.length} out of {pagination.totalItems} orders
                </div>
                <div className="order-1 sm:order-2 w-full sm:w-auto">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Order Details Modal */}
        <OrderDetailsModal
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          order={selectedOrder}
        />

        {/* Mobile Filters */}
        {mobileFiltersOpen && <MobileFiltersModal />}
      </div>
    </AdminLayout>
  );
};

export default OrdersTab;